import { flowBuilderConstants } from '../../../constants/FlowBuilder';
import { translateLabel } from '../../../modules/journey-canvas/utility/helpers/PrepareJourneyHelpers';
import { drawCanvasImage, setImageSrc } from '../helpers';
import { CanvasElementConfig, ConnectorIcon, FlowButton, PositionWithSize } from '../TypeDefs';
import { CanvasElement } from './CanvasElement';
import { drawBody, drawConnectorIcon, drawNameText } from './helpers';

/**
 * Represents a StepElement in a canvas.
 */
export class StepElement extends CanvasElement {
  /**
   * The count of services.
   *
   * @type {number}
   * @private
   */
  private serviceCount = 0;

  /**
   * The count of events.
   *
   * @type {number}
   * @private
   */
  private eventCount = 0;

  /**
   * The count of CEMs.
   *
   * @type {number}
   * @private
   */
  private cemCount = 0;

  /**
   * The image for the service count icon.
   *
   * @type {Image}
   * @private
   */
  private serviceCountIconImage = new Image();

  /**
   * The image for the event count icon.
   *
   * @type {Image}
   * @private
   */
  private eventCountIconImage = new Image();

  /**
   * The image for the CEM count icon.
   *
   * @type {Image}
   * @private
   */
  private cemCountIconImage = new Image();

  /**
   * The image for the menu dots.
   *
   * @type {Image}
   * @private
   */
  private menuDotsImage = new Image();

  /**
   * The image for the step icon.
   *
   * @type {Image}
   * @private
   */
  private stepIconImage = new Image();

  /**
   * The image for the step connector icon.
   *
   * @type {Image}
   * @private
   */
  private stepConnectorIconImage = new Image();

  private dotIconPosition: PositionWithSize = {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: 0,
    width: 0
  };

  draw(ctx: CanvasRenderingContext2D) {
    const nameFont = `500 ${this.config.nameFontSize}px '${this.config.fontFamily}'`;
    const countTextFont = `400 14px '${this.config.fontFamily}'`;
    const nameLineHeight = this.config.nameFontSize * this.config.lineHeightRatio;

    const centerY = this.top + this.config.padding + nameLineHeight;
    const iconSize = nameLineHeight;
    const iconX = this.left + this.config.padding;
    const nameTextX = iconX + iconSize + this.config.padding - 5;
    const iconY = this.top + this.config.padding + nameLineHeight - iconSize / 2;

    const countIconY = iconY + iconSize + this.config.padding * 2;

    /**
     * @description Draw the body of the element.
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
     * @param {number} cardRadius - The radius of the element's card.
     * @param {object} config - The configuration object for the element.
     * @param {number} descriptionLineHeight - The line height of the description.
     * @param {boolean} drawingStep - Indicates if the element is being drawn as a step.
     * @param {boolean} isError - Indicates if the element is in an error state.
     * @param {boolean} isSelected - Indicates if the element is selected.
     * @param {boolean} isStackStyled - Indicates if the element is stack styled.
     * @param {number} left - The left position of the element.
     * @param {string} mDescription - The description of the element.
     * @param {number} top - The top position of the element.
     * @returns {object} The height of the element.
     */
    const { height } = drawBody({
      ctx,
      cardRadius: flowBuilderConstants.stepCardRadius,
      config: this.config,
      descriptionLineHeight: nameLineHeight + iconSize,
      drawingStep: true,
      isError: this.isError,
      isSelected: this.isSelected,
      isStackStyled: this.isStackStyled,
      left: this.left,
      mDescription: this.mDescription,
      top: this.top,
    });
    this.finalBottom = this.top + height;

    // draw the icon
    drawCanvasImage(ctx, this.stepIconImage, iconX, iconY, iconSize, iconSize);

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    drawCanvasImage(ctx, this.serviceCountIconImage, iconX, countIconY, iconSize, iconSize);
    this.drawCountText(ctx, nameFont, this.serviceCount.toString(), iconX + iconSize + 6, countIconY + (iconSize / 2));
    this.drawCountText(ctx, countTextFont, translateLabel('step_elements.txt_service'), iconX, countIconY + iconSize + this.config.padding);

    drawCanvasImage(ctx, this.eventCountIconImage, iconX + (iconSize * 4), countIconY, iconSize, iconSize);
    this.drawCountText(ctx, nameFont, this.eventCount.toString(), iconX + (iconSize * 5) + 6, countIconY + (iconSize / 2));
    this.drawCountText(ctx, countTextFont, translateLabel('step_elements.txt_events'), iconX + (iconSize * 4), countIconY + iconSize + this.config.padding);

    drawCanvasImage(ctx, this.cemCountIconImage, iconX + (iconSize * 8), countIconY, iconSize, iconSize);
    this.drawCountText(ctx, nameFont, this.cemCount.toString(), iconX + (iconSize * 9) + 6, countIconY + (iconSize / 2));
    this.drawCountText(ctx, countTextFont, translateLabel('step_elements.txt_compensatory'), iconX + (iconSize * 8), countIconY + iconSize + this.config.padding);

    this.dotIconPosition = {
      top: iconY + 4,
      left: this.right - (iconSize * 2),
      right: this.right - (iconSize * 2) + 4,
      bottom: iconY + 4 + 14,
      height: 14,
      width: 4
    };
    drawCanvasImage(ctx, this.menuDotsImage, this.dotIconPosition.left, this.dotIconPosition.top, this.dotIconPosition.width, this.dotIconPosition.height);

    /**
     * @method drawNameText
     * @description Draw the name text of the element.
     * @param {number} centerY - The y-coordinate of the center.
     * @param {object} config - The configuration object for the element.
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
     * @param {boolean} isSelected - Indicates if the element is selected.
     * @param {number} left - The left position of the element.
     * @param {string} mName - The name of the element.
     * @param {string} nameFont - The font for the name text.
     * @param {number} nameTextX - The x-coordinate of the name text.
     */
    drawNameText({
      centerY,
      config: this.config,
      ctx,
      isSelected: this.isSelected,
      left: this.left,
      mName: this.mName,
      nameFont,
      nameTextX
    });

    /**
     * @method drawConnectorIcon
     * @description Draw the connector icon of the element.
     * @param {object} config - The configuration object for the element.
     * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
     * @param {object} icon - The icon object.
     * @param {string} icon.src - The source of the icon image.
     * @param {number} icon.width - The width of the icon.
     * @param {number} icon.height - The height of the icon.
     * @param {Image} icon.image - The image object for the icon.
     * @param {boolean} inConnector - Indicates if the connector is an incoming connector.
     * @param {boolean} stepConnector - Indicates if the connector is a step connector.
     * @param {number} xCoordinate - The x-coordinate of the connector.
     * @param {number} yCoordinate - The y-coordinate of the connector.
     */
    drawConnectorIcon({
      config: this.config,
      ctx,
      icon: {
        src: this.stepConnectorIconImage.src,
        width: 24,
        height: 24,
        image: this.stepConnectorIconImage
      },
      inConnector: false,
      stepConnector: true,
      xCoordinate: this.outgoingHX,
      yCoordinate: this.outgoingHY
    });
  }

  /**
 * @method isMouseOnStepMenuIcon
 * @description Check if mouse is on step card menu icon
 * @param mouseX mouse x position
 * @param mouseY mouse y position
 * @returns true / false
 */
  isMouseOnStepMenuIcon(mouseX: number, mouseY: number): boolean {
    return (
      mouseX >= this.dotIconPosition.left - 20
      && mouseX <= this.dotIconPosition.right + 20
      && mouseY >= this.dotIconPosition.top - 20
      && mouseY <= this.dotIconPosition.bottom + 20
    );
  }

  /**
   * @method drawCountText
   * @description Draw the count text on the canvas.
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
   * @param {string} countFont - The font for the count text.
   * @param {string} countText - The text to be displayed as the count.
   * @param {number} countX - The x-coordinate of the count text.
   * @param {number} countY - The y-coordinate of the count text.
   */
  drawCountText(ctx: CanvasRenderingContext2D, countFont: string, countText: string, countX: number, countY: number) {
    ctx.font = countFont;
    ctx.fillStyle = this.config.nameColor;
    ctx.fillText(
      countText,
      countX,
      countY + 2,
    );
  }

  /**
   * @method createStep
   * @description Create a StepElement instance.
   * @param {object} params - The parameters for creating the StepElement.
   * @param {string} params.elementId - The ID of the element.
   * @param {number} [params.rev] - The revision number.
   * @param {number} params.left - The left position of the element.
   * @param {number} params.top - The top position of the element.
   * @param {string} params.name - The name of the element.
   * @param {boolean} [params.isSelected=false] - Indicates if the element is selected.
   * @param {string} params.stepIcon - The icon for the step.
   * @param {boolean} [params.isRoot=false] - Indicates if the element is a root element.
   * @param {boolean} [params.isStackStyled=false] - Indicates if the element is stack styled.
   * @param {ConnectorIcon} [params.incomingIcon] - The icon for the incoming connector.
   * @param {ConnectorIcon} [params.outgoingIcon] - The icon for the outgoing connector.
   * @param {FlowButton[]} [params.buttons=[]] - The buttons associated with the element.
   * @param {CanvasElementConfig} params.config - The configuration object for the element.
   * @param {number} params.serviceCount - The count of services.
   * @param {number} params.eventCount - The count of events.
   * @param {number} params.cemCount - The count of CEMs.
   * @param {string} params.serviceCountIcon - The icon for the service count.
   * @param {string} params.eventCountIcon - The icon for the event count.
   * @param {string} params.cemCountIcon - The icon for the CEM count.
   * @param {string} params.menuDots - The icon for the menu dots.
   * @param {string} params.stepConnectorIcon - The icon for the step connector.
   * @returns {StepElement} The created StepElement instance.
   */
  public static createStep({
    elementId,
    rev,
    left,
    top,
    name,
    isSelected = false,
    stepIcon,
    isRoot = false,
    isStackStyled = false,
    incomingIcon,
    outgoingIcon,
    buttons = [],
    config,
    serviceCount,
    eventCount,
    cemCount,
    serviceCountIcon,
    eventCountIcon,
    cemCountIcon,
    menuDots,
    stepConnectorIcon
  }: {
    elementId: string,
    rev?: number,
    left: number,
    top: number,
    name: string,
    isSelected?: boolean,
    stepIcon: string,
    isRoot?: boolean,
    isStackStyled?: boolean,
    incomingIcon?: ConnectorIcon,
    outgoingIcon?: ConnectorIcon,
    buttons?: FlowButton[],
    config: CanvasElementConfig,
    serviceCount: number,
    eventCount: number,
    cemCount: number,
    serviceCountIcon: string,
    eventCountIcon: string,
    cemCountIcon: string,
    menuDots: string,
    stepConnectorIcon: string
  }) {
    const instance = new StepElement(config);
    instance.elementId = elementId;
    instance.rev = rev;
    instance.left = left;
    instance.top = top;
    instance.isSelected = isSelected;
    instance.isRoot = isRoot;
    instance.isStackStyled = isStackStyled;
    instance.serviceCount = serviceCount;
    instance.eventCount = eventCount;
    instance.cemCount = cemCount;

    buttons.forEach((b) => {
      const img = new Image();
      setImageSrc(img, b.icon);
      instance.buttons.push({
        ...b,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        image: img
      });
    });

    instance.calculateBound();

    instance.mName.text = name;
    setImageSrc(instance.iconImage, stepIcon);
    setImageSrc(instance.serviceCountIconImage, serviceCountIcon);
    setImageSrc(instance.eventCountIconImage, eventCountIcon);
    setImageSrc(instance.cemCountIconImage, cemCountIcon);
    setImageSrc(instance.menuDotsImage, menuDots);
    setImageSrc(instance.stepIconImage, stepIcon);
    setImageSrc(instance.stepConnectorIconImage, stepConnectorIcon);

    if (incomingIcon) {
      const image = new Image();
      setImageSrc(image, incomingIcon.src);
      instance.incomingIcon = {
        ...incomingIcon,
        image
      };
    }
    if (outgoingIcon) {
      const image = new Image();
      setImageSrc(image, outgoingIcon.src);
      instance.outgoingIcon = {
        ...outgoingIcon,
        image
      };
    }

    return instance;
  }
}
